import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Home = () => {
  const [postcode, setPostcode] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const { isAuthenticated } = useAuth();

  const handlePostcodeCheck = async (e) => {
    e.preventDefault();
    setChecking(true);
    setCheckResult(null);
    try {
      const response = await authAPI.checkPostcode(postcode);
      setCheckResult(response.data);
    } catch (error) {
      setCheckResult({ inServiceArea: false, message: 'Something went wrong — please try again.' });
    } finally {
      setChecking(false);
    }
  };

  const services = [
    { icon: '🧺', title: 'Laundry Subscription', body: 'Wash, dry & fold on a weekly or fortnightly schedule. Perfect for busy households.', price: 'from £14.99', to: isAuthenticated ? '/book-subscription' : '/register' },
    { icon: '👔', title: 'Shirts & Trousers', body: 'Professionally washed and pressed, ready to wear. A favourite with busy professionals.', price: 'from £11.99', to: isAuthenticated ? '/book-subscription' : '/register' },
    { icon: '🧥', title: 'Dry Cleaning', body: 'Suits, dresses, coats, duvets and more — cleaned with care and paid for per item.', price: 'pay per item', to: isAuthenticated ? '/book-alacarte' : '/register' }
  ];

  const steps = [
    { t: 'Book online', d: 'Choose a service and a collection time that suits you.' },
    { t: 'We collect', d: 'Our friendly team picks up from your doorstep.' },
    { t: 'We clean', d: 'Washed, pressed and cared for with gentle products.' },
    { t: 'We deliver', d: 'Fresh and folded, back to you — usually within 48 hours.' }
  ];

  const reasons = [
    { i: '🚐', t: 'Free collection & delivery', d: 'Right from your door — no trips to the launderette.' },
    { i: '⏱️', t: '~48 hour turnaround', d: 'Most orders are back with you within two days.' },
    { i: '🌿', t: 'Gentle & eco-friendly', d: 'Kind to your clothes and to the planet.' },
    { i: '🤝', t: 'A trusted local team', d: 'Real people from your area who care about the details.' }
  ];

  // NOTE: placeholder reviews — replace with real customer testimonials before launch.
  const testimonials = [
    { name: 'Sarah T.', area: 'Clapham', quote: 'Honestly life-changing for a busy family. They collect on Monday and it\'s all back beautifully folded by Wednesday.' },
    { name: 'James M.', area: 'Richmond', quote: 'My shirts have never looked sharper, and the team is so friendly. I wouldn\'t go anywhere else now.' },
    { name: 'Priya K.', area: 'Weybridge', quote: 'Booked a last-minute dry clean for a wedding outfit — collected, cleaned and delivered without a hitch. Brilliant.' }
  ];

  const areas = ['South West London', 'Central London', 'West London', 'Heathrow', 'Staines', 'Weybridge'];

  return (
    <div>
      {/* Hero */}
      <section className="hero">
        <div className="hero-inner">
          <div>
            <span className="hero-eyebrow">👋 Your friendly local laundry service</span>
            <h1>Leave the laundry <span className="hl">to us.</span></h1>
            <p className="lead">
              We collect from your door, wash, dry and fold, then bring it all back fresh —
              usually within 48 hours. Win back your weekend.
            </p>
            <div className="hero-buttons">
              <Link to={isAuthenticated ? '/book-alacarte' : '/register'} className="btn btn-accent btn-lg">Get started</Link>
              <a href="#how" className="btn btn-outline btn-lg">How it works</a>
            </div>
            <div className="trust-row">
              <span>🚐 Free collection &amp; delivery</span>
              <span>⏱️ Ready in ~48 hours</span>
              <span>🤝 Trusted local team</span>
            </div>
          </div>

          <div className="hero-card">
            <h3>Do we cover your area?</h3>
            <p className="muted">Pop in your postcode and we'll check right away.</p>
            <form onSubmit={handlePostcodeCheck}>
              <div className="form-group" style={{ marginBottom: '0.8rem' }}>
                <input
                  type="text"
                  placeholder="e.g. SW1A 1AA"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={checking}>
                {checking ? 'Checking…' : 'Check my postcode'}
              </button>
            </form>

            {checkResult && (
              <div className={checkResult.inServiceArea ? 'success-message' : 'error-message'}>
                {checkResult.inServiceArea ? (
                  <>
                    <strong>Great news — we cover {checkResult.areaName}! 🎉</strong>
                    {!isAuthenticated && (
                      <div style={{ marginTop: '0.8rem' }}>
                        <Link to="/register" className="btn btn-accent btn-block">Create your account</Link>
                      </div>
                    )}
                  </>
                ) : (
                  <span>{checkResult.message}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Services */}
      <div className="container">
        <h2 className="section-title">What we can take off your hands</h2>
        <p className="section-sub">Pick a regular subscription or a one-off clean — whatever suits your week.</p>
        <div className="cards-grid">
          {services.map((s) => (
            <div className="card" key={s.title}>
              <div className="feature-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <span className="price-small">{s.price}</span>
                <Link to={s.to} className="btn btn-primary">Book now</Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="section-tint" id="how">
        <div className="container">
          <h2 className="section-title">How it works</h2>
          <p className="section-sub">Four simple steps — no fuss, no queues, no lugging baskets around.</p>
          <div className="cards-grid steps">
            {steps.map((step, i) => (
              <div className="card" key={step.t}>
                <div className="step-num">{i + 1}</div>
                <h3>{step.t}</h3>
                <p style={{ marginBottom: 0 }}>{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why us */}
      <div className="container">
        <h2 className="section-title">Why our neighbours love us</h2>
        <div className="cards-grid">
          {reasons.map((r) => (
            <div className="card" key={r.t}>
              <div className="feature-icon">{r.i}</div>
              <h3>{r.t}</h3>
              <p style={{ marginBottom: 0 }}>{r.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <div className="section-tint">
        <div className="container">
          <h2 className="section-title">Loved by locals</h2>
          <p className="section-sub">Don't just take our word for it.</p>
          <div className="cards-grid">
            {testimonials.map((t) => (
              <div className="card" key={t.name}>
                <div style={{ color: 'var(--accent)', fontSize: '1.1rem', letterSpacing: '2px', marginBottom: '0.6rem' }}>★★★★★</div>
                <p style={{ fontStyle: 'italic', color: 'var(--ink)', marginBottom: '1rem' }}>“{t.quote}”</p>
                <strong>{t.name}</strong>
                <span style={{ color: 'var(--ink-soft)' }}> · {t.area}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service areas */}
      <div className="container">
        <div>
          <h2 className="section-title">Where we collect &amp; deliver</h2>
          <p className="section-sub">We're proud to serve these areas — and we're adding more all the time.</p>
          <div className="pill-list">
            {areas.map((a) => (
              <span className="pill" key={a}><span className="check">✓</span>{a}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="container" style={{ textAlign: 'center' }}>
        <div className="card" style={{ background: 'var(--primary)', border: 'none', color: '#fff', padding: '2.6rem' }}>
          <h2 style={{ color: '#fff', fontSize: '1.9rem', marginBottom: '0.6rem' }}>Ready to win back your weekend?</h2>
          <p style={{ color: 'rgba(255,255,255,0.92)', marginBottom: '1.4rem' }}>
            Join your neighbours and let us handle the laundry.
          </p>
          <Link to={isAuthenticated ? '/book-alacarte' : '/register'} className="btn btn-accent btn-lg">Get started today</Link>
        </div>
      </div>
    </div>
  );
};

export default Home;
